// Information architecture for the Wix/Squarespace-style admin dashboard.
// Each group renders as a collapsible section in the sidebar.
// `id` is the deep-link key used in the ?view=<id> URL param.
//
// IA aligned with the CMS Independence Sprint (Phase 2) target groups:
//   Overview · Website · Content · Media · SEO & GEO · Marketing ·
//   Customers · Analytics · Operations · Settings · Help
//
// Legacy panels with newer replacements are removed from the sidebar but kept
// in the view-ID union and resolvable via LEGACY_VIEW_REDIRECTS so deep links
// from old bookmarks still land on the right place.

import {
  LayoutDashboard,
  Globe,
  FileText,
  Image as ImageIcon,
  Search,
  Megaphone,
  Inbox,
  BarChart3,
  Activity,
  Settings,
  LifeBuoy,
  type LucideIcon,
} from "lucide-react";

export type AdminViewId =
  // Overview
  | "overview"
  // Website
  | "cms.pages-v2"
  | "cms.scheduled"
  | "cms.editor"
  | "cms.preview"
  | "cms.business-info"
  | "settings.entity"
  | "cms.hero"
  | "cms.blocks-v2"
  | "cms.forms"
  | "redirects.v2"
  // Content
  | "cms.content"
  | "cms.testimonials"
  | "cms.services"
  | "cms.areas"
  // Media
  | "cms.media-v2"
  | "cms.gallery"
  // SEO & GEO
  | "seo.checklist"
  | "seo.per-page"
  | "seo.geo"
  | "seo.nap"
  | "seo.schema"
  | "seo.schema-manager"
  | "seo.schema-checker"
  | "seo.jsonld-preview"
  | "seo.cannibalisation"
  | "seo.sitemap"
  | "seo.indexing"
  | "seo.gsc"
  | "seo.gbp"
  | "seo.citations"
  | "seo.changelog"
  // Marketing
  | "cms.offers"
  | "outreach.prospects"
  // Customers
  | "leads.crm"
  | "leads.flow-health"
  | "leads.test"
  | "leads.sources"
  | "leads.cta-qa"
  | "leads.driver-links"
  | "email.settings"
  | "email.delivery"
  | "email.logs"
  | "email.templates"
  // Analytics
  | "analytics.overview"
  | "analytics.deep-dive"
  | "analytics.areas"
  | "analytics.export"
  | "analytics.ga4"
  | "vitals"
  // Operations
  | "health.centre"
  | "health.overview"
  | "health.links"
  | "health.schema"
  | "health.a11y"
  | "health.performance"
  | "health.history"
  | "ops.lighthouse"
  | "ops.integrations"
  | "ops.backups"
  | "ops.badge-debug"
  | "ops.frontend-errors"
  | "settings.stability"
  | "settings.staging"
  | "settings.launch"
  // Settings
  | "settings.mot"
  | "settings.reviews"
  | "settings.area-list"
  | "settings.compliance"
  | "settings.trust"
  | "settings.routing"
  | "settings.gbp-authority"
  | "settings.users"
  | "settings.security"
  | "settings.access-log"
  | "settings.verification"
  // Help

  | "ops.help"
  // Legacy (retired from sidebar, still resolvable for deep links)
  | "cms.pages"
  | "cms.blog"
  | "cms.media"
  | "cms.blocks"
  | "redirects"
  | "leads.inbox"
  | "settings.business"
  | "settings.contact";

export type AdminGroup = {
  id: string;
  label: string;
  icon: LucideIcon;
  items: { id: AdminViewId; label: string; badge?: string }[];
};

export const ADMIN_GROUPS: AdminGroup[] = [
  {
    id: "overview",
    label: "Overview",
    icon: LayoutDashboard,
    items: [{ id: "overview", label: "Dashboard" }],
  },
  {
    id: "website",
    label: "Website",
    icon: Globe,
    items: [
      { id: "cms.pages-v2", label: "Pages" },
      { id: "cms.scheduled", label: "Scheduled publishing" },
      { id: "cms.editor", label: "Visual editor" },
      { id: "cms.preview", label: "Live preview" },
      { id: "settings.entity", label: "Business details" },
      { id: "cms.business-info", label: "Business info" },
      { id: "cms.hero", label: "Hero overlay" },
      { id: "cms.blocks-v2", label: "Global blocks" },
      { id: "cms.forms", label: "Form builder" },
      { id: "redirects.v2", label: "Redirects" },
    ],
  },
  {
    id: "content",
    label: "Content",
    icon: FileText,
    items: [
      { id: "cms.content", label: "Blog & guides" },
      { id: "cms.testimonials", label: "Testimonials" },
      { id: "cms.services", label: "Services" },
      { id: "cms.areas", label: "Service areas" },
    ],
  },
  {
    id: "media",
    label: "Media",
    icon: ImageIcon,
    items: [
      { id: "cms.media-v2", label: "Media library" },
      { id: "cms.gallery", label: "Gallery photos" },
    ],
  },
  {
    id: "seo",
    label: "SEO & GEO",
    icon: Search,
    items: [
      { id: "seo.checklist", label: "Checklist & fixes" },
      { id: "seo.per-page", label: "Per-page editor" },
      { id: "seo.jsonld-preview", label: "JSON-LD preview" },
      { id: "seo.cannibalisation", label: "Cannibalisation audit" },
      { id: "seo.schema-manager", label: "Schema manager" },
      { id: "seo.schema", label: "Schema validator" },
      { id: "seo.schema-checker", label: "Schema checker" },
      { id: "seo.geo", label: "GEO answer-ready" },
      { id: "seo.nap", label: "NAP source of truth" },
      { id: "seo.sitemap", label: "Sitemap" },
      { id: "seo.indexing", label: "Indexing URLs" },
      { id: "seo.gsc", label: "GSC monitor" },
      { id: "seo.gbp", label: "GBP weekly" },
      { id: "seo.citations", label: "Citations" },
      { id: "seo.changelog", label: "Change log" },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: Megaphone,
    items: [
      { id: "cms.offers", label: "Offers & promotions" },
      { id: "outreach.prospects", label: "Backlinks & outreach" },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: Inbox,
    items: [
      { id: "leads.crm", label: "Leads CRM" },
      { id: "leads.flow-health", label: "Flow health" },
      { id: "leads.test", label: "Test enquiry" },
      { id: "leads.sources", label: "Source pages" },
      { id: "leads.cta-qa", label: "CTA strategy QA" },
      { id: "leads.driver-links", label: "Driver link tracking" },
      { id: "email.settings", label: "Email settings & routing" },
      { id: "email.delivery", label: "Email delivery monitor" },
      { id: "email.logs", label: "Email logs" },
      { id: "email.templates", label: "Email template tester" },
    ],
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: BarChart3,
    items: [
      { id: "analytics.overview", label: "Dashboard" },
      { id: "analytics.deep-dive", label: "Deep dive" },
      { id: "analytics.areas", label: "Area conversion" },
      { id: "analytics.export", label: "Area export" },
      { id: "analytics.ga4", label: "GA4 event tester" },
      { id: "vitals", label: "Core Web Vitals" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: Activity,
    items: [
      { id: "health.centre", label: "Health centre" },
      { id: "health.overview", label: "Health overview" },
      { id: "health.links", label: "Broken links" },
      { id: "health.schema", label: "Schema results" },
      { id: "health.a11y", label: "Accessibility" },
      { id: "health.performance", label: "Performance" },
      { id: "health.history", label: "Run history" },
      { id: "ops.lighthouse", label: "Lighthouse monitor" },
      { id: "ops.integrations", label: "Integration health" },
      { id: "ops.backups", label: "Backups & exports" },
      { id: "ops.badge-debug", label: "Badge debug" },
      { id: "ops.frontend-errors", label: "Frontend errors" },
      { id: "settings.stability", label: "Site stability" },
      { id: "settings.staging", label: "Staging verification" },
      { id: "settings.launch", label: "Launch checklist" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    items: [
      { id: "settings.mot", label: "MOT settings" },
      { id: "settings.reviews", label: "Reviews (local)" },
      { id: "settings.area-list", label: "Areas (local)" },
      { id: "settings.compliance", label: "Compliance" },
      { id: "settings.trust", label: "Trust content" },
      { id: "settings.routing", label: "Routing targets" },
      { id: "settings.gbp-authority", label: "GBP ownership" },
      { id: "settings.users", label: "Admin users" },
      { id: "settings.security", label: "Security hardening" },
      { id: "settings.access-log", label: "Admin access log" },
      { id: "settings.verification", label: "Address & hours verification" },
    ],

  },
  {
    id: "help",
    label: "Help",
    icon: LifeBuoy,
    items: [{ id: "ops.help", label: "Admin guides" }],
  },
];

/**
 * Maps retired sidebar IDs to their current replacement. Used by the admin
 * router so old bookmarks (e.g. `?view=cms.pages`) still resolve to the new
 * canonical panel without 404ing.
 */
export const LEGACY_VIEW_REDIRECTS: Partial<Record<AdminViewId, AdminViewId>> = {
  "cms.pages": "cms.pages-v2",
  "cms.blog": "cms.content",
  "cms.media": "cms.media-v2",
  "cms.blocks": "cms.blocks-v2",
  redirects: "redirects.v2",
  "leads.inbox": "leads.crm",
  "settings.business": "settings.entity",
  "settings.contact": "settings.entity",
};

export const DEFAULT_VIEW: AdminViewId = "overview";

/** Resolve a possibly-legacy view ID to the canonical current ID. */
export function resolveViewId(view: AdminViewId): AdminViewId {
  return LEGACY_VIEW_REDIRECTS[view] ?? view;
}

export function findGroupForView(view: AdminViewId): AdminGroup | undefined {
  const canonical = resolveViewId(view);
  return ADMIN_GROUPS.find((g) => g.items.some((i) => i.id === canonical));
}

export function findItemLabel(view: AdminViewId): string {
  const canonical = resolveViewId(view);
  for (const g of ADMIN_GROUPS) {
    const it = g.items.find((i) => i.id === canonical);
    if (it) return it.label;
  }
  return "Dashboard";
}
